import { Capacitor } from '@capacitor/core';
import type { AppLocale } from '../i18n/locale';
import packageJson from '../../package.json';

const STORAGE_KEY = 'pouches-review-state';
const SESSION_COUNTED_KEY = 'pouches-review-session-counted';
const LAUNCH_THRESHOLD = 10;
const COOLDOWN_DAYS = 30;
const PROMPT_DELAY_MS = 5000;
const APPLE_APP_ID = '6759413081';

const AIRTABLE_FORM_URLS: Record<AppLocale, string> = {
  ja: 'https://airtable.com/appIpAuwoVRCxzjcr/shrJql8sZUaekKD6Y',
  en: 'https://airtable.com/appIpAuwoVRCxzjcr/shrWhPOixBEspTNwS',
  zh: 'https://airtable.com/appIpAuwoVRCxzjcr/shrYSDAi2CVNPkCCs',
};

const APP_NAME = 'POUCHES';

type ReviewState = {
  launchCount: number;
  lastRequestedAt: string | null;
};

export function getWebFeedbackOsDescription(): string {
  if (typeof navigator === 'undefined') return 'Web / Unknown';

  const ua = navigator.userAgent;
  const platform = navigator.platform || '';

  if (/iPhone|iPad|iPod/.test(ua)) {
    const m = ua.match(/(?:iPhone OS|CPU OS) ([\d_]+)/);
    const ver = m ? m[1].replace(/_/g, '.') : '?';
    return `Web / iOS ${ver}`;
  }
  if (/Android/.test(ua)) {
    const m = ua.match(/Android ([\d.]+)/);
    return `Web / Android ${m?.[1] ?? '?'}`;
  }
  if (/Mac/.test(platform) || /Macintosh/.test(ua)) {
    const m = ua.match(/Mac OS X ([\d_]+)/);
    const ver = m ? m[1].replace(/_/g, '.') : '?';
    return `Web / macOS ${ver}`;
  }
  if (/Win/.test(platform) || /Windows/.test(ua)) {
    const m = ua.match(/Windows NT ([\d.]+)/);
    const nt = m?.[1];
    const winLabel =
      nt === '10.0' ? '10/11' : nt === '6.3' ? '8.1' : nt === '6.2' ? '8' : nt ?? '?';
    return `Web / Windows ${winLabel}`;
  }
  if (/Linux/.test(platform) || /Linux/.test(ua)) {
    return 'Web / Linux';
  }
  return `Web / ${platform || 'Unknown'}`;
}

export function getAirtableFeedbackUrl(
  locale: AppLocale,
  options?: { version?: string; os?: string },
): string {
  const base = AIRTABLE_FORM_URLS[locale] ?? AIRTABLE_FORM_URLS.en;
  const sep = base.includes('?') ? '&' : '?';
  const params = new URLSearchParams({
    prefill_App: APP_NAME,
    prefill_Version: options?.version ?? packageJson.version,
    prefill_OS: options?.os ?? getWebFeedbackOsDescription(),
  });
  return `${base}${sep}${params.toString()}`;
}

async function openExternalUrl(url: string): Promise<void> {
  const plugin = (window as { Capacitor?: { Plugins?: { Browser?: { open: (o: { url: string }) => Promise<void> } } } })
    .Capacitor?.Plugins?.Browser;
  if (plugin?.open) {
    try {
      await plugin.open({ url });
      return;
    } catch {
      // fall through
    }
  }
  window.open(url, '_blank');
}

export function isNativeReviewPlatform(): boolean {
  const win = window as {
    electron?: { isElectron?: boolean };
  };
  if (win.electron?.isElectron) return true;
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function readState(): ReviewState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ReviewState>;
      return {
        launchCount: Math.max(0, Number(parsed.launchCount) || 0),
        lastRequestedAt: parsed.lastRequestedAt ?? null,
      };
    }
  } catch {
    // localStorage 不可でも起動処理は続ける
  }
  return { launchCount: 0, lastRequestedAt: null };
}

function writeState(state: ReviewState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // 無視
  }
}

function daysSince(isoDate: string): number {
  return (Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24);
}

function recordLaunchIfNewSession(): number {
  try {
    if (sessionStorage.getItem(SESSION_COUNTED_KEY)) {
      return readState().launchCount;
    }
    sessionStorage.setItem(SESSION_COUNTED_KEY, '1');
  } catch {
    // sessionStorage 不可なら毎回加算（ネイティブでは通常あり）
  }
  const state = readState();
  state.launchCount += 1;
  writeState(state);
  return state.launchCount;
}

export function shouldRequestReview(launchCount: number, state: ReviewState): boolean {
  if (launchCount < LAUNCH_THRESHOLD) return false;
  if (state.lastRequestedAt && daysSince(state.lastRequestedAt) < COOLDOWN_DAYS) {
    return false;
  }
  return true;
}

async function requestNativeReview(): Promise<boolean> {
  const win = window as {
    electron?: { requestReview?: () => Promise<{ ok?: boolean }> };
    Capacitor?: { Plugins?: { InAppReview?: { requestReview: () => Promise<void> } } };
  };

  if (win.electron?.requestReview) {
    const result = await win.electron.requestReview();
    return result?.ok === true;
  }

  const plugin = win.Capacitor?.Plugins?.InAppReview;
  if (plugin?.requestReview) {
    await plugin.requestReview();
    return true;
  }

  return false;
}

async function openAppleReviewPage(): Promise<void> {
  const lang = (navigator.language || 'en').toLowerCase();
  const storefront = lang.startsWith('ja') ? 'jp' : lang.startsWith('zh') ? 'cn' : 'us';
  const mt = Capacitor.getPlatform() === 'ios' ? 8 : 12;
  const url = `https://apps.apple.com/${storefront}/app/id${APPLE_APP_ID}?action=write-review&mt=${mt}`;
  await openExternalUrl(url);
}

export async function requestReviewFromMenu(locale: AppLocale): Promise<void> {
  if (!isNativeReviewPlatform()) {
    await openExternalUrl(getAirtableFeedbackUrl(locale));
    return;
  }
  const win = window as {
    electron?: { requestReview?: () => Promise<{ ok?: boolean }> };
  };
  if (win.electron?.requestReview) {
    try {
      await win.electron.requestReview();
    } catch (error) {
      console.warn('[AppReview] menu request failed', error);
    }
    return;
  }
  try {
    await openAppleReviewPage();
  } catch (error) {
    console.warn('[AppReview] menu request failed', error);
  }
}

/**
 * ネイティブ版の起動時に呼ぶ。10回目以降の起動で、前回依頼から30日経過していれば
 * 初期化完了の少し後にストアのレビュー依頼を出す。Web では何もしない。
 */
export async function maybeRequestReviewOnLaunch(): Promise<void> {
  if (!isNativeReviewPlatform()) return;

  const state = readState();
  const launchCount = recordLaunchIfNewSession();
  if (!shouldRequestReview(launchCount, state)) return;

  await new Promise((resolve) => setTimeout(resolve, PROMPT_DELAY_MS));

  try {
    const ok = await requestNativeReview();
    if (ok) {
      writeState({ ...readState(), lastRequestedAt: new Date().toISOString() });
    }
  } catch (error) {
    console.warn('[AppReview] request failed', error);
  }
}

export const APP_REVIEW_LAUNCH_THRESHOLD = LAUNCH_THRESHOLD;
export const APP_REVIEW_COOLDOWN_DAYS = COOLDOWN_DAYS;
